import { Heading } from './heading';
import { Link } from './link';
import { MediaUpload } from './media-upload';
import { Paragraph } from './paragraph';
import { Text } from './text';
import { TextList } from './text-list';
import type { ElementNode, MediaUploadElementNode, TextNode } from './types';

const isTextNode = (node: TextNode | ElementNode): node is TextNode =>
  'text' in node;

export const RichContent = ({
  indentLevel = 0,
  nodes,
}: {
  indentLevel?: number;
  nodes: (TextNode | ElementNode | null)[];
}) => {
  return (
    <>
      {/* Using index as key is acceptable here because:
          1. CMS content is static and never reordered at runtime
          2. Payload CMS nodes don't have unique IDs
          3. Content only changes on page navigation (full re-render) */}
      {nodes
        .filter((node): node is TextNode | ElementNode => node !== null)
        .map((node, index) => {
          if (isTextNode(node)) {
            return <Text key={index} {...node} />;
          }
          const children = 'children' in node ? (node.children ?? []) : [];
          const type = 'type' in node ? node.type : undefined;
          switch (type) {
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
              return (
                <Heading key={index} type={type}>
                  <RichContent nodes={children} />
                </Heading>
              );
            case 'link': {
              const url = 'url' in node ? (node.url ?? '#') : '#';
              const newTab = 'newTab' in node ? (node.newTab ?? false) : false;
              return (
                <Link key={index} url={url} newTab={newTab}>
                  <RichContent nodes={children} />
                </Link>
              );
            }
            case 'ul':
            case 'ol':
              return (
                <TextList key={index} type={type}>
                  <RichContent nodes={children} />
                </TextList>
              );
            case 'li':
              return (
                <li key={index}>
                  <RichContent nodes={children} />
                </li>
              );
            case 'upload':
              return (
                <MediaUpload key={index} {...(node as MediaUploadElementNode)} />
              );
            case 'indent':
              return (
                <RichContent
                  key={index}
                  indentLevel={indentLevel + 1}
                  nodes={children}
                />
              );
            // Note: Payload CMS Lexical editor does NOT send `type: 'paragraph'` for paragraph nodes.
            // Paragraphs are identified by the absence of a `type` field (type?: undefined).
            // This was verified by querying the actual CMS API - only `type: 'upload'` exists.
            // Therefore, paragraph nodes fall through to the default case below.
            // See: packages/shared-types/src/cms/rich-content.ts for type definition.
            //
            // Why no exhaustiveness check (assertNever)?
            // TypeScript's exhaustiveness checking requires all union members to have a discriminant.
            // Since ParagraphElementNode has `type?: undefined`, it matches any object with `children`
            // that doesn't have a recognized `type`. This makes exhaustiveness checking impossible
            // without type assertions that would defeat the purpose.
            default:
              // Fallback for unknown types - render as paragraph
              // Log in all environments since CMS content is maintained independently
              // and new node types may only be discovered in production
              if (type) {
                console.warn(`[RichContent] Unknown node type: ${type}`, node);
              }
              return (
                <Paragraph key={index} indentLevel={indentLevel}>
                  <RichContent nodes={children} />
                </Paragraph>
              );
          }
        })}
    </>
  );
};

export * from './types';
